/**
 * Google Places API Integration for Store Locator
 * Fetches reviews, ratings, hours, and other data from Google Places API
 */

(function() {
  'use strict';

  // Configuration
  const SELECTED_STORE_KEY = 'titanSelectedStore';
  const config = {
    // Use proxy URL ONLY if explicitly configured, otherwise use direct API (FREE with $200/month credit)
    useProxy: false, // Default to direct API - no backend needed!
    proxyUrl: '', // Only used if provided in settings
    apiKey: '', // Will be set from section settings
    directApiUrl: 'https://maps.googleapis.com/maps/api/place/details/json',
    fields: 'name,rating,user_ratings_total,formatted_address,formatted_phone_number,opening_hours,reviews,place_id,geometry,url',
    language: 'en'
  };
  

  function saveSelectedStore(storeData) {
    if (!storeData) return;
    try {
      sessionStorage.setItem(SELECTED_STORE_KEY, JSON.stringify(storeData));
    } catch (error) {
      console.warn('Unable to persist selected store:', error);
    }
  }

  function getStoredStoreData() {
    try {
      const raw = sessionStorage.getItem(SELECTED_STORE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      console.warn('Unable to read selected store:', error);
      return null;
    }
  }

  function clearStoredStoreData() {
    try {
      sessionStorage.removeItem(SELECTED_STORE_KEY);
    } catch (error) {
      console.warn('Unable to clear selected store:', error);
    }
  }

  function getStoreCodeFromQuery() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('store');
    } catch (error) {
      return null;
    }
  }

  function parseJsonScript(elementId) {
    try {
      const el = document.getElementById(elementId);
      if (!el) return null;
      return JSON.parse(el.textContent);
    } catch (error) {
      console.warn(`Failed to parse JSON from #${elementId}:`, error);
      return null;
    }
  }

  /**
   * Get section settings from the page
   */
  function getSectionSettings() {
    try {
      const settingsScript = document.getElementById('store-locator-settings');
      if (settingsScript) {
        return JSON.parse(settingsScript.textContent);
      }
    } catch (e) {
      console.error('Error parsing section settings:', e);
    }
    return null;
  }

  /**
   * Initialize configuration from section settings
   */
  function initConfig() {
    const settings = getSectionSettings();
    if (settings) {
      if (settings.google_places_api_key) {
        config.apiKey = settings.google_places_api_key;
      } else {
        console.warn('[Store Places API] No Google Places API key found in settings');
      }
      if (settings.places_api_proxy_url && settings.places_api_proxy_url.trim() !== '') {
        config.proxyUrl = settings.places_api_proxy_url;
        config.useProxy = true;
      } else {
        config.useProxy = false;
      }
    } else {
      console.warn('[Store Places API] No settings found');
    }
  }

  /**
   * Get current day of week (0 = Sunday, 6 = Saturday)
   */
  function getCurrentDay() {
    return new Date().getDay();
  }

  /**
   * Format operating hours
   */
  function formatHours(openingHours) {
    if (!openingHours || !openingHours.weekday_text) {
      return { status: 'Hours not available', isOpen: null };
    }

    const currentDay = getCurrentDay();
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDayName = days[currentDay];
    
    // Find today's hours
    const todayHours = openingHours.weekday_text.find(day => 
      day.toLowerCase().startsWith(currentDayName.toLowerCase())
    );

    if (!todayHours) {
      return { status: 'Hours not available', isOpen: null };
    }

    // Extract time range (e.g., "Monday: 9:00 AM – 10:00 PM")
    const timeMatch = todayHours.match(/(\d{1,2}:\d{2}\s?(?:AM|PM))\s*–\s*(\d{1,2}:\d{2}\s?(?:AM|PM))/i);
    
    if (timeMatch) {
      const openTime = timeMatch[1];
      const closeTime = timeMatch[2];
      const status = openingHours.open_now ? 'Open Today' : 'Closed';
      
      return {
        status: status,
        hours: `${openTime} – ${closeTime}`,
        isOpen: openingHours.open_now,
        fullHours: openingHours.weekday_text
      };
    }

    return { status: todayHours, isOpen: openingHours.open_now };
  }

  /**
   * Render star rating - Always show 5 stars
   */
  function renderStars(rating, container) {
    if (!rating || !container) return;
    
    container.innerHTML = '';
    
    // Calculate how many stars to show as full
    // For ratings 4.5+, show all 5 stars as full (matches Google's display)
    const fullStars = rating >= 4.5 ? 5 : Math.round(rating);
    
    // Always show exactly 5 stars
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        // Full star
        container.innerHTML += '<span>★</span>';
      } else {
        // Empty star with reduced opacity
        container.innerHTML += '<span style="opacity: 0.25;">★</span>';
      }
    }
  }

  /**
   * Update store card with Google Places data
   */
  function updateStoreCard(storeCard, placeData) {
    const storeIndex = storeCard.getAttribute('data-store-index');
    
    
    // Update rating and review count
    if (placeData.rating) {
      const ratingValue = document.getElementById(`store-rating-value-${storeIndex}`);
      const ratingStars = document.getElementById(`store-rating-stars-${storeIndex}`);
      
      
      if (ratingValue) {
        ratingValue.textContent = placeData.rating.toFixed(1);
      } else {
        console.warn(`[Store Places API] Rating value element not found: store-rating-value-${storeIndex}`);
      }
      if (ratingStars) {
        renderStars(placeData.rating, ratingStars);
      } else {
        console.warn(`[Store Places API] Rating stars element not found: store-rating-stars-${storeIndex}`);
      }
    } else {
      console.warn(`[Store Places API] No rating data in placeData for store ${storeIndex}`);
    }
    
    // Update review count if available
    if (placeData.user_ratings_total) {
      const ratingContainer = document.getElementById(`store-rating-${storeIndex}`);
      if (ratingContainer) {
        // Check if review count element already exists
        let reviewCountEl = ratingContainer.querySelector('.store-review-count');
        if (!reviewCountEl) {
          reviewCountEl = document.createElement('span');
          reviewCountEl.className = 'store-review-count';
          ratingContainer.appendChild(reviewCountEl);
        }
        reviewCountEl.textContent = `(${placeData.user_ratings_total} Reviews)`;
        reviewCountEl.style.display = 'inline-block';
        reviewCountEl.style.marginLeft = '8px';
        reviewCountEl.style.fontSize = '14px';
        reviewCountEl.style.color = '#53372b';
      } else {
        console.warn(`[Store Places API] Rating container element not found: store-rating-${storeIndex}`);
      }
    } else {
    }

    // Update address (use Google's formatted address if available)
    if (placeData.formatted_address) {
      const addressEl = document.getElementById(`store-address-${storeIndex}`);
      if (addressEl) {
        addressEl.textContent = placeData.formatted_address;
      }
    }

    // Update phone (use Google's formatted phone if available)
    if (placeData.formatted_phone_number) {
      const phoneEl = document.getElementById(`store-phone-${storeIndex}`);
      if (phoneEl) {
        const phoneNumber = placeData.formatted_phone_number.replace(/\s/g, '');
        phoneEl.innerHTML = `<a href="tel:${phoneNumber}">${placeData.formatted_phone_number}</a>`;
      }
    }

    // Update hours
    if (placeData.opening_hours) {
      const hoursData = formatHours(placeData.opening_hours);
      const hoursEl = document.getElementById(`store-hours-${storeIndex}`);
      
      if (hoursEl) {
        const statusEl = hoursEl.querySelector('.store-hours-text');
        if (statusEl) {
          // Show "Open Today" or "Closed" status
          if (hoursData.isOpen === true) {
            statusEl.textContent = 'Open Today';
            statusEl.classList.add('open');
            statusEl.classList.remove('closed');
          } else if (hoursData.isOpen === false) {
            statusEl.textContent = 'Closed';
            statusEl.classList.add('closed');
            statusEl.classList.remove('open');
          } else {
            statusEl.textContent = hoursData.status || 'Hours not available';
            statusEl.classList.remove('open', 'closed');
          }

          // Add dropdown for full hours if available
          if (hoursData.fullHours && hoursData.fullHours.length > 0) {
            const dropdownBtn = hoursEl.querySelector('.store-hours-dropdown');
            if (dropdownBtn) {
              dropdownBtn.style.display = 'inline-flex';
              // Remove existing listeners to avoid duplicates
              const newDropdownBtn = dropdownBtn.cloneNode(true);
              dropdownBtn.parentNode.replaceChild(newDropdownBtn, dropdownBtn);
              newDropdownBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                showHoursModal(hoursData.fullHours);
              });
            }
          }
        }
      }
    }

    // Update directions link - using same format as store-details.liquid
    const directionsBtn = document.getElementById(`store-directions-${storeIndex}`);
    if (directionsBtn) {
      let directionsUrl = '';
      
      // Use address-based Google Maps URL (same format as store-details.liquid)
      if (placeData.formatted_address) {
        directionsUrl = `https://www.google.com/maps?q=${encodeURIComponent(placeData.formatted_address)}`;
      } else if (placeData.place_id) {
        // Fallback to place_id if formatted_address not available
        directionsUrl = `https://www.google.com/maps?q=place_id:${encodeURIComponent(placeData.place_id)}`;
      } else if (placeData.geometry && placeData.geometry.location) {
        // Use coordinates if available
        const lat = placeData.geometry.location.lat;
        const lng = placeData.geometry.location.lng;
        directionsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
      } else {
        // Final fallback to store address from data attribute
        const address = storeCard.getAttribute('data-store-address');
        if (address) {
          directionsUrl = `https://www.google.com/maps?q=${encodeURIComponent(address)}`;
        }
      }
      
      if (directionsUrl) {
        directionsBtn.href = directionsUrl;
        directionsBtn.setAttribute('href', directionsUrl);
        // Ensure it opens in new tab
        if (!directionsBtn.hasAttribute('target')) {
          directionsBtn.setAttribute('target', '_blank');
        }
        if (!directionsBtn.hasAttribute('rel')) {
          directionsBtn.setAttribute('rel', 'noopener noreferrer');
        }
        
        // Store the directions URL in the card's data attribute for later retrieval
        storeCard.setAttribute('data-store-directions-url', directionsUrl);
        
        // Also save to sessionStorage and localStorage using store code as key
        const storeCode = storeCard.getAttribute('data-store-code');
        if (storeCode) {
          try {
            sessionStorage.setItem(`store-directions-${storeCode}`, directionsUrl);
            localStorage.setItem(`store-directions-${storeCode}`, directionsUrl);
          } catch (error) {
            console.error('[Store Places API] Could not save directions URL to storage:', error);
          }
        }
      }
    }

    // Remove loading state
    storeCard.classList.remove('store-card-loading');
    
    // Log success message
    if (placeData.rating || placeData.user_ratings_total) {
    }
  }

  /**
   * Attach click navigation to store detail page
   */
  function attachCardNavigation(storeCard) {
    if (!storeCard) return;

    // Prevent card navigation when clicking on action buttons
    const actionButtons = storeCard.querySelectorAll('.store-action-btn, .store-action-btn a');
    actionButtons.forEach(btn => {
      btn.addEventListener('click', function(e) {
        e.stopPropagation();
        e.stopImmediatePropagation();
        // Allow the link to work normally
        return true;
      }, true); // Use capture phase to ensure it fires first
    });

    storeCard.addEventListener('click', function(event) {
      const target = event.target;
      if (!target) return;

      // Ignore clicks on action buttons or links - check more thoroughly
      const clickedButton = target.closest('.store-action-btn');
      const clickedLink = target.closest('a');
      
      if (clickedButton || clickedLink) {
        // Don't prevent default - let the link work naturally
        // Just stop propagation to prevent card navigation
        event.stopPropagation();
        return;
      }

      const storeData = {
        index: storeCard.getAttribute('data-store-index'),
        name: storeCard.getAttribute('data-store-name'),
        address: storeCard.getAttribute('data-store-address'),
        placeId: storeCard.getAttribute('data-store-place-id'),
        contact: storeCard.getAttribute('data-store-contact'),
        code: storeCard.getAttribute('data-store-code'),
        emirate: storeCard.getAttribute('data-store-emirate'),
        country: storeCard.getAttribute('data-store-country')
      };

      // Get the directions URL from the card's data attribute or directions button
      const directionsUrlFromCard = storeCard.getAttribute('data-store-directions-url');
      if (directionsUrlFromCard) {
        storeData.directionsUrl = directionsUrlFromCard;
      } else {
        // Try to get from directions button href
        const directionsBtn = storeCard.querySelector('[id^="store-directions-"]');
        if (directionsBtn && directionsBtn.href && directionsBtn.href !== '#' && !directionsBtn.href.includes('#')) {
          storeData.directionsUrl = directionsBtn.href;
        } else {
          // Generate directions URL from address if button doesn't have it yet
          if (storeData.address) {
            storeData.directionsUrl = 'https://www.google.com/maps?q=' + encodeURIComponent(storeData.address);
          }
        }
      }

      saveSelectedStore(storeData);

      const detailUrl = storeCard.getAttribute('data-store-detail-url') || '/pages/store-details';
      let redirectUrl = detailUrl;
      try {
        const url = new URL(detailUrl, window.location.origin);
        if (storeData.code) {
          url.searchParams.set('store', storeData.code);
        } else if (storeData.placeId) {
          url.searchParams.set('place_id', storeData.placeId);
        }
        redirectUrl = url.toString();
      } catch (error) {
        // Fallback to original detailUrl if URL parsing fails
      }
      window.location.href = redirectUrl;
    });
  }

  function findMetaStore(metaobjects, storeData) {
    if (!Array.isArray(metaobjects) || metaobjects.length === 0 || !storeData) {
      console.warn('[Store Places API] findMetaStore: Invalid inputs', {
        metaobjectsLength: metaobjects?.length,
        storeData: storeData
      });
      return null;
    }

    const normalize = (value) => (value || '').toString().trim().toLowerCase();

    const code = normalize(storeData.code);
    
    if (code) {
      const matchByCode = metaobjects.find(item => {
        const itemCode = normalize(item.store_code);
        const matches = itemCode === code;
        if (matches) {
        }
        return matches;
      });
      if (matchByCode) {
        return matchByCode;
      } else {
        console.warn('[Store Places API] No match found by code. Available codes:', 
          metaobjects.map(item => ({ 
            code: item.store_code, 
            normalized: normalize(item.store_code),
            name: item.store_name 
          }))
        );
      }
    }

    const placeId = normalize(storeData.placeId);
    if (placeId) {
      const matchByPlace = metaobjects.find(item => normalize(item.google_place_id) === placeId || normalize(item.place_id) === placeId);
      if (matchByPlace) {
        return matchByPlace;
      }
    }

    const name = normalize(storeData.name);
    if (name) {
      const matchByName = metaobjects.find(item => normalize(item.store_name) === name);
      if (matchByName) {
        return matchByName;
      }
    }

    console.error('[Store Places API] ❌ No store match found for:', storeData);
    return null;
  }

  function updateBreadcrumbCurrent(name) {
    const breadcrumb = document.querySelector('[data-breadcrumb-current]');
    if (breadcrumb) {
      breadcrumb.textContent = name || '';
    }
  }

  function setDetailText(detailRoot, selector, value) {
    if (!detailRoot) return;
    const elements = detailRoot.querySelectorAll(selector);
    if (elements.length === 0) return;
    elements.forEach(el => {
      el.textContent = value || '';
    });
  }

  function setDetailHTML(detailRoot, selector, value) {
    if (!detailRoot) return;
    const elements = detailRoot.querySelectorAll(selector);
    if (elements.length === 0) return;
    elements.forEach(el => {
      el.innerHTML = value || '';
    });
  }

  function setInfoItemVisibility(detailRoot, key, shouldShow) {
    if (!detailRoot) return;
    const item = detailRoot.querySelector(`[data-info-item="${key}"]`);
    if (!item) return;
    item.classList.toggle('is-hidden', !shouldShow);
  }

  function setElementVisibility(element, shouldShow) {
    if (!element) return;
    element.classList.toggle('is-hidden', !shouldShow);
  }

  function updateBreadcrumbSub(label) {
    const breadcrumb = document.querySelector('[data-breadcrumb-sub]');
    if (breadcrumb) {
      breadcrumb.textContent = label || '';
    }
  }

  /**
   * Update directions link based on current address in DOM or saved directions URL
   * Similar to how it's done in store-location-list.liquid
   */
  function updateDirectionsLinkFromAddress(detailRoot) {
    if (!detailRoot) {
      return;
    }
    
    // First, try to get saved directions URL from sessionStorage
    let directionsUrl = null;
    try {
      const storedData = getStoredStoreData();
      if (storedData && storedData.directionsUrl) {
        directionsUrl = storedData.directionsUrl;
      } else if (storedData && storedData.address) {
        // If no directionsUrl but we have address, generate it
        const encodedAddress = encodeURIComponent(storedData.address);
        directionsUrl = 'https://www.google.com/maps?q=' + encodedAddress;
      }
    } catch (error) {
      console.error('[Store Places API] Could not read stored directions URL:', error);
    }
    
    // If no saved URL, generate from address in DOM
    if (!directionsUrl) {
      // Try multiple selectors to find the address element
      let addressElement = detailRoot.querySelector('[data-store-detail="address"]');
      if (!addressElement) {
        // Try finding by info-item
        const addressInfoItem = detailRoot.querySelector('[data-info-item="address"]');
        if (addressInfoItem) {
          addressElement = addressInfoItem.querySelector('[data-store-detail="address"]');
        }
      }
      
      const address = addressElement ? addressElement.textContent.trim() : '';
      
      
      if (!address || address === '') {
        console.error('[Store Places API] No address found to update directions link');
        return;
      }
      
      // Create Google Maps URL exactly like store-location-list.liquid
      const encodedAddress = encodeURIComponent(address);
      directionsUrl = 'https://www.google.com/maps?q=' + encodedAddress;
    }
    
    const directionLinks = detailRoot.querySelectorAll('[data-store-detail="directions-link"]');
    
    if (directionLinks.length > 0) {
      directionLinks.forEach((link, index) => {
        // Check if this is an appointment button - if so, don't set Google Maps URL
        if (link.classList.contains('store-appointment-btn')) {
          console.error('[Store Places API] Skipping appointment button');
          return;
        }

        // Directly set href attribute (same approach as store-location-list.liquid)
        link.href = directionsUrl;
        link.setAttribute('href', directionsUrl);
        
        // Ensure it opens in new tab
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        
      });
    } else {
      console.error('[Store Places API] No direction links found!');
    }
  }

  function populateDetailBase(detailRoot, storeInfo) {
    if (!detailRoot || !storeInfo) return;

    const displayName = storeInfo.store_name || storeInfo.name || 'Titan Store';
    setDetailText(detailRoot, '[data-store-detail="title"]', displayName);
    updateBreadcrumbCurrent(displayName);

    if (typeof document !== 'undefined') {
      try {
        const baseTitle = document.title || '';
        if (!baseTitle.toLowerCase().includes(displayName.toLowerCase())) {
          document.title = `${displayName} | Titan UAE`;
        }
      } catch (error) {
        // noop
      }
    }

    const emirate = storeInfo.emirate || '';
    updateBreadcrumbSub(emirate);

    const address = storeInfo.address || '';
    if (address) {
      setDetailText(detailRoot, '[data-store-detail="address"]', address);
      setInfoItemVisibility(detailRoot, 'address', Boolean(address));
      if(document.querySelector('.address-icon')){
      document.querySelector('.address-icon').style.display="block";
      }
      
      // Update directions link immediately after setting address
      // Use multiple timeouts to ensure DOM is updated and link is set
      setTimeout(() => {
        updateDirectionsLinkFromAddress(detailRoot);
      }, 0);
      
      setTimeout(() => {
        updateDirectionsLinkFromAddress(detailRoot);
      }, 50);
      
      setTimeout(() => {
        updateDirectionsLinkFromAddress(detailRoot);
      }, 200);
    } else {
      // Still try to update from sessionStorage if available
      if(document.querySelector('.address-icon')){
      document.querySelector('.address-icon').style.display="none";
      }
      setTimeout(() => {
        updateDirectionsLinkFromAddress(detailRoot);
      }, 100);
    }

    let displayPhone = storeInfo.contact || '';
    // Ensure displayPhone is a string
    if (displayPhone && typeof displayPhone !== 'string') {
      displayPhone = String(displayPhone);
    }
    if (displayPhone && typeof displayPhone === 'string' && !displayPhone.startsWith('+')) {
      displayPhone = `+${displayPhone}`;
    }
    const phoneLinks = detailRoot.querySelectorAll('[data-store-detail="phone-link"]');
    if (phoneLinks.length > 0) {
      phoneLinks.forEach(link => {
        if (displayPhone) {
          link.textContent = displayPhone;
          link.setAttribute('href', `tel:${displayPhone.replace(/\s/g, '')}`);
        } else {
          link.textContent = '';
          link.removeAttribute('href');
        }
      });
    }
    setInfoItemVisibility(detailRoot, 'phone', Boolean(displayPhone));

    setDetailText(detailRoot, '[data-store-detail="hours-status"]', 'Open Today');
    setDetailText(detailRoot, '[data-store-detail="hours-range"]', '');
    setInfoItemVisibility(detailRoot, 'hours', true);
  }

  /**
   * Initialize directions button fallback for detail page
   */
  function initializeDetailDirectionsButtonFallback(detailRoot, address) {
    if (!detailRoot) {
      return;
    }
    
    // First, try to get saved directions URL from sessionStorage
    let directionsUrl = null;
    try {
      const storedData = getStoredStoreData();
      if (storedData && storedData.directionsUrl) {
        directionsUrl = storedData.directionsUrl;
      } else if (storedData && storedData.address) {
        // If no directionsUrl but we have address, generate it
        const encodedAddress = encodeURIComponent(storedData.address);
        directionsUrl = 'https://www.google.com/maps?q=' + encodedAddress;
      }
    } catch (error) {
      console.error('[Store Places API] Could not read stored directions URL:', error);
    }
    
    // If no saved URL, generate from address parameter or DOM
    if (!directionsUrl) {
      // Use the address parameter if provided, otherwise read from DOM
      let addressToUse = address;
      
      if (!addressToUse || addressToUse.trim() === '') {
        // Try multiple selectors to find the address element
        let addressElement = detailRoot.querySelector('[data-store-detail="address"]');
        if (!addressElement) {
          const addressInfoItem = detailRoot.querySelector('[data-info-item="address"]');
          if (addressInfoItem) {
            addressElement = addressInfoItem.querySelector('[data-store-detail="address"]');
          }
        }
        addressToUse = addressElement ? addressElement.textContent.trim() : '';
      }
      
      
      if (!addressToUse || addressToUse.trim() === '') {
        return;
      }
      
      // Set address in DOM if not already set
      let addressElement = detailRoot.querySelector('[data-store-detail="address"]');
      if (!addressElement) {
        const addressInfoItem = detailRoot.querySelector('[data-info-item="address"]');
        if (addressInfoItem) {
          addressElement = addressInfoItem.querySelector('[data-store-detail="address"]');
        }
      }
      
      if (addressElement && (!addressElement.textContent.trim() || addressElement.textContent.trim() === '')) {
        addressElement.textContent = addressToUse;
      }
      
      // Create Google Maps URL exactly like store-location-list.liquid
      const encodedAddress = encodeURIComponent(addressToUse);
      directionsUrl = 'https://www.google.com/maps?q=' + encodedAddress;
    }
    
    // Update directions link directly (same approach as store-location-list.liquid)
    const directionLinks = detailRoot.querySelectorAll('[data-store-detail="directions-link"]');
    
    if (directionLinks.length > 0) {
      directionLinks.forEach((link, index) => {
        // Check if this is an appointment button - if so, don't set Google Maps URL
        if (link.classList.contains('store-appointment-btn')) {
          return;
        }

        // Directly set href attribute
        link.href = directionsUrl;
        link.setAttribute('href', directionsUrl);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        
      });
    } else {
      console.error('[Store Places API] ERROR: No direction links found in initializeDetailDirectionsButtonFallback!');
    }
  }

  async function initializeStoreDetail() {
    const detailRoot = document.getElementById('store-detail-section');
    if (!detailRoot) {
      return;
    }

    detailRoot.classList.add('store-detail-loading');
    setDetailText(detailRoot, '[data-store-detail="status-message"]', '');

    initConfig();

    const metaobjects = parseJsonScript('titan-store-metaobjects') || [];
    
    // Prioritize URL parameter over sessionStorage when present
    let storeData = null;
    const queryCode = getStoreCodeFromQuery();
    
    if (queryCode) {
      // URL parameter takes priority - clear any old sessionStorage data
      storeData = { code: queryCode };
    } else {
      // Fall back to sessionStorage if no URL parameter
      storeData = getStoredStoreData();
      if (storeData) {
      }
    }

    if (!storeData) {
      try {
        const currentUrl = new URL(window.location.href);
        const placeIdParam = currentUrl.searchParams.get('place_id');
        if (placeIdParam) {
          storeData = { placeId: placeIdParam };
        }
      } catch (error) {
        // ignore URL parsing errors
      }
    }

    if (!storeData) {
      const fallbackCode = detailRoot.getAttribute('data-default-store-code');
      if (fallbackCode) {
        storeData = { code: fallbackCode };
      }
    }

    if (!storeData) {
      setDetailText(detailRoot, '[data-store-detail="status-message"]', 'Store details are not available. Please return to the store list and select a store.');
      detailRoot.classList.add('store-detail-error');
      detailRoot.classList.remove('store-detail-loading');
      // Still try to update directions link from dummy data
      updateDirectionsLinkFromAddress(detailRoot);
      return;
    }

    const metaStore = findMetaStore(metaobjects, storeData);
    if (metaStore) {
      const appointmentUrl =
        metaStore.appointment_url ||
        metaStore.book_appointment_link ||
        metaStore.book_an_appointment_link ||
        storeData.appointment_url;
      storeData = Object.assign({}, storeData, {
        store_name: metaStore.store_name || storeData.store_name,
        name: metaStore.store_name || storeData.name,
        address: metaStore.address || metaStore.formatted_address || storeData.address,
        emirate: metaStore.emirate || metaStore.emirate_name || storeData.emirate,
        placeId: metaStore.google_place_id || metaStore.place_id || storeData.placeId,
        appointment_url: appointmentUrl,
        contact: metaStore.store_contact_no || metaStore.contact || storeData.contact,
        code: metaStore.store_code || storeData.code
      });
      saveSelectedStore(storeData);
    }

    populateDetailBase(detailRoot, storeData);

    // Initialize directions button IMMEDIATELY with address fallback (before fetching Google Places data)
    // This ensures the link is set even if address comes from sessionStorage
    if (storeData.address) {
      initializeDetailDirectionsButtonFallback(detailRoot, storeData.address);
    } else {
      // Try to get from sessionStorage first
      try {
        const storedData = getStoredStoreData();
        if (storedData && storedData.address) {
          initializeDetailDirectionsButtonFallback(detailRoot, storedData.address);
        } else {
          // If no address in storeData, try reading from DOM (might be set by populateDetailBase)
          setTimeout(() => {
            updateDirectionsLinkFromAddress(detailRoot);
          }, 50);
        }
      } catch (error) {
        setTimeout(() => {
          updateDirectionsLinkFromAddress(detailRoot);
        }, 50);
      }
    }

    const ratingCardEl = detailRoot.querySelector('[data-store-detail="rating-card"]');
    const reviewBodyEl = detailRoot.querySelector('[data-store-detail="review-body"]');
    const reviewsLinkEl = detailRoot.querySelector('[data-store-detail="reviews-link"]');
    setElementVisibility(ratingCardEl, false);
    setElementVisibility(reviewBodyEl, false);
    setElementVisibility(reviewsLinkEl, false);

    // Update appointment CTA if url available
    const appointmentLink = detailRoot.querySelector('[data-store-detail="appointment-link"]');
    if (appointmentLink) {
      const appointmentUrl = storeData.appointment_url || appointmentLink.getAttribute('data-default-url');
      if (appointmentUrl) {
        appointmentLink.setAttribute('href', appointmentUrl);
      }
    }

    if (storeData.placeId) {
      const placeData = await fetchPlaceDetails(storeData.placeId, storeData.name, storeData.address);
      if (placeData) {
        populateDetailFromPlace(detailRoot, placeData);
        detailRoot.classList.remove('store-detail-error');
        setDetailText(detailRoot, '[data-store-detail="status-message"]', '');
      } else if (storeData.address) {
        setDetailText(detailRoot, '[data-store-detail="address"]', storeData.address);
        // Update directions link after setting address
        setTimeout(() => {
          updateDirectionsLinkFromAddress(detailRoot);
        }, 100);
      }
    } else if (storeData.address) {
      setDetailText(detailRoot, '[data-store-detail="address"]', storeData.address);
      // Update directions link after setting address
      setTimeout(() => {
        updateDirectionsLinkFromAddress(detailRoot);
      }, 100);
    }

    detailRoot.classList.remove('store-detail-loading');
    
    // Final check: ensure directions link is updated even if address was set elsewhere
    setTimeout(() => {
      updateDirectionsLinkFromAddress(detailRoot);
    }, 100);
  }

  function populateDetailFromPlace(detailRoot, placeData) {
    if (!detailRoot || !placeData) return;

    const ratingCard = detailRoot.querySelector('[data-store-detail="rating-card"]');
    const reviewBody = detailRoot.querySelector('[data-store-detail="review-body"]');
    const reviewsLinkEls = detailRoot.querySelectorAll('[data-store-detail="reviews-link"]');

    const starContainers = detailRoot.querySelectorAll('[data-store-detail="rating-stars"]');
    starContainers.forEach(container => {
      container.innerHTML = '';
    });

    const rating = placeData.rating;
    const hasRating = Boolean(rating);

    if (rating) {
      setDetailText(detailRoot, '[data-store-detail="rating-value"]', rating.toFixed(1));
      starContainers.forEach(container => renderStars(rating, container));
    } else {
      setDetailText(detailRoot, '[data-store-detail="rating-value"]', '');
    }

    if (placeData.user_ratings_total) {
      setDetailText(detailRoot, '[data-store-detail="ratings-count"]', `(${placeData.user_ratings_total} Reviews)`);
    } else {
      setDetailText(detailRoot, '[data-store-detail="ratings-count"]', '');
    }

    if (placeData.formatted_address) {
      setDetailText(detailRoot, '[data-store-detail="address"]', placeData.formatted_address);
      // Update directions link after updating address
      updateDirectionsLinkFromAddress(detailRoot);
    }

    if (placeData.formatted_phone_number) {
      const cleanedPhone = placeData.formatted_phone_number.replace(/\s/g, '');
      const phoneLinks = detailRoot.querySelectorAll('[data-store-detail="phone-link"]');
      if (phoneLinks.length > 0) {
        phoneLinks.forEach(link => {
          link.textContent = placeData.formatted_phone_number;
          link.setAttribute('href', `tel:${cleanedPhone}`);
        });
      } else {
        setDetailText(detailRoot, '[data-store-detail="phone"]', placeData.formatted_phone_number);
      }
      setInfoItemVisibility(detailRoot, 'phone', true);
    }

    if (placeData.opening_hours) {
      const hoursData = formatHours(placeData.opening_hours);
      const status = hoursData.status || (hoursData.isOpen ? 'Open Today' : 'Closed');
      const statusEls = detailRoot.querySelectorAll('[data-store-detail="hours-status"]');
      statusEls.forEach(el => {
        el.textContent = status;
        el.classList.toggle('open', hoursData.isOpen === true);
        el.classList.toggle('closed', hoursData.isOpen === false);
      });

      if (hoursData.hours) {
        setDetailText(detailRoot, '[data-store-detail="hours-range"]', hoursData.hours);
      }
      const hasHours = Boolean(hoursData.status || hoursData.hours);
      setInfoItemVisibility(detailRoot, 'hours', hasHours);
    } else {
      const statusEls = detailRoot.querySelectorAll('[data-store-detail="hours-status"]');
      statusEls.forEach(el => {
        el.textContent = '';
        el.classList.remove('open', 'closed');
      });
      setDetailText(detailRoot, '[data-store-detail="hours-range"]', '');
      setInfoItemVisibility(detailRoot, 'hours', false);
    }

    let reviewUrl = '';
    if (placeData.url) {
      reviewUrl = placeData.url;
    } else if (placeData.place_id) {
      reviewUrl = `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(placeData.place_id)}`;
    }
    reviewsLinkEls.forEach(link => {
      if (reviewUrl) {
        link.setAttribute('href', reviewUrl);
      } else {
        link.removeAttribute('href');
      }
      link.classList.toggle('is-hidden', !reviewUrl);
    });

    // Set up directions link to Google Maps - using same format as store-details.liquid
    const directionLinks = detailRoot.querySelectorAll('[data-store-detail="directions-link"]');
    if (directionLinks.length > 0) {
      directionLinks.forEach(link => {
        // Check if this is an appointment button - if so, don't set Google Maps URL
        if (link.classList.contains('store-appointment-btn')) {
          // Appointment booking will handle the click
          return;
        }

        let directionsUrl = '';
        
        // Use address-based Google Maps URL (same format as store-details.liquid: https://www.google.com/maps?q=)
    if (placeData.formatted_address) {
      directionsUrl = `https://www.google.com/maps?q=${encodeURIComponent(placeData.formatted_address)}`;
    } else if (placeData.place_id) {
      // Fallback to place_id if formatted_address not available
      directionsUrl = `https://www.google.com/maps?q=place_id:${encodeURIComponent(placeData.place_id)}`;
    } else if (placeData.geometry && placeData.geometry.location) {
      // Use coordinates if available
      const lat = placeData.geometry.location.lat;
      const lng = placeData.geometry.location.lng;
      directionsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    } else {
      // Try to get address from DOM element
      const fallbackAddress = detailRoot.querySelector('[data-store-detail="address"]')?.textContent?.trim();
      if (fallbackAddress) {
        directionsUrl = `https://www.google.com/maps?q=${encodeURIComponent(fallbackAddress)}`;
      }
    }
        
        if (directionsUrl) {
          link.setAttribute('href', directionsUrl);
          // Ensure it opens in new tab (already set in HTML, but ensure it's there)
          if (!link.hasAttribute('target')) {
            link.setAttribute('target', '_blank');
          }
          if (!link.hasAttribute('rel')) {
            link.setAttribute('rel', 'noopener noreferrer');
          }
        } else {
          // Hide link if no valid URL
          link.style.display = 'none';
        }
      });
    }

    let hasReview = false;
    if (Array.isArray(placeData.reviews) && placeData.reviews.length > 0) {
      const featuredReview = placeData.reviews[0];
      setDetailText(detailRoot, '[data-store-detail="review-author"]', featuredReview.author_name || '');
      if (featuredReview.text) {
        setDetailText(detailRoot, '[data-store-detail="review-text"]', featuredReview.text);
        hasReview = true;
      }
    } else {
      setDetailText(detailRoot, '[data-store-detail="review-author"]', '');
      setDetailText(detailRoot, '[data-store-detail="review-text"]', '');
    }

    setElementVisibility(reviewBody, hasReview);
    setElementVisibility(ratingCard, hasRating || hasReview);
  }

  /**
   * Show hours modal
   */
  function showHoursModal(hours) {
    // Create modal (simple implementation)
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;
    
    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      padding: 24px;
      border-radius: 8px;
      max-width: 400px;
      width: 90%;
    `;
    
    content.innerHTML = `
      <h3 style="margin-top: 0;">Operating Hours</h3>
      <ul style="list-style: none; padding: 0;">
        ${hours.map(day => `<li style="padding: 8px 0; border-bottom: 1px solid #eee;">${day}</li>`).join('')}
      </ul>
      <button style="margin-top: 16px; padding: 8px 16px; cursor: pointer;">Close</button>
    `;
    
    modal.appendChild(content);
    document.body.appendChild(modal);
    
    const closeBtn = content.querySelector('button');
    closeBtn.addEventListener('click', () => {
      document.body.removeChild(modal);
    });
    
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  }

  /**
   * Fetch place details from Google Places API
   */
  async function fetchPlaceDetails(placeId, storeName, storeAddress) {
    try {
      let url;
      let options = {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      };

      if (config.useProxy && config.proxyUrl) {
        // Use proxy endpoint (recommended for production)
        url = `${config.proxyUrl}?place_id=${encodeURIComponent(placeId)}`;
      } else {
        // Direct API call (requires API key restrictions)
        if (!config.apiKey) {
          console.warn('[Store Places API] Google Places API key not configured');
          return null;
        }
        url = `${config.directApiUrl}?place_id=${encodeURIComponent(placeId)}&fields=${config.fields}&key=${config.apiKey}&language=${config.language}`;
      }

      const response = await fetch(url, options);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'OK' && data.result) {
        return data.result;
      } else if (data.error_message) {
        console.error('[Store Places API] Google Places API error:', data.error_message);
        console.error('[Store Places API] Full error response:', data);
        return null;
      } else {
        console.warn('[Store Places API] Unexpected response from Places API:', data);
        return null;
      }
    } catch (error) {
      console.error('[Store Places API] Error fetching place details:', error);
      console.error('[Store Places API] Error details:', {
        message: error.message,
        stack: error.stack
      });
      return null;
    }
  }

  /**
   * Search for place by name and address (fallback if no place_id)
   */
  async function searchPlaceByName(storeName, storeAddress) {
    try {
      // This would require Places API Text Search
      // For now, return null and use address-based directions
      console.warn('Place search by name not implemented. Using address fallback.');
      return null;
    } catch (error) {
      console.error('Error searching for place:', error);
      return null;
    }
  }

  /**
   * Initialize directions button with address fallback (before Google Places API loads)
   */
  function initializeDirectionsButtonFallback(storeCard, storeIndex) {
    const directionsBtn = document.getElementById(`store-directions-${storeIndex}`);
    if (directionsBtn) {
      // Check if this is an appointment button (has store-appointment-btn class)
      // If so, don't set up Google Maps URL - let appointment booking handle it
      if (directionsBtn.classList.contains('store-appointment-btn')) {
        // Appointment booking will handle the click
        return;
      }

      const storeAddress = storeCard.getAttribute('data-store-address');
      if (storeAddress) {
        // Use same format as store-details.liquid
        const directionsUrl = `https://www.google.com/maps?q=${encodeURIComponent(storeAddress)}`;
        directionsBtn.href = directionsUrl;
        directionsBtn.setAttribute('href', directionsUrl);
        // Ensure it opens in new tab
        directionsBtn.setAttribute('target', '_blank');
        directionsBtn.setAttribute('rel', 'noopener noreferrer');
        
        // Store the directions URL in the card's data attribute for later retrieval
        storeCard.setAttribute('data-store-directions-url', directionsUrl);
        
        // Also save to sessionStorage and localStorage using store code as key
        const storeCode = storeCard.getAttribute('data-store-code');
        if (storeCode) {
          try {
            sessionStorage.setItem(`store-directions-${storeCode}`, directionsUrl);
            localStorage.setItem(`store-directions-${storeCode}`, directionsUrl);
          } catch (error) {
            console.error('[Store Places API] Could not save directions URL to storage:', error);
          }
        }
        
        // Add click handler to ensure navigation works and prevent card navigation
        directionsBtn.addEventListener('click', function(e) {
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // If href is still # or empty, use window.open as fallback
          if (!this.href || this.href === '#' || this.href.endsWith('#')) {
            e.preventDefault();
            window.open(directionsUrl, '_blank', 'noopener,noreferrer');
            return false;
          }
          
          // Otherwise let the link work naturally
          return true;
        }, true); // Use capture phase
      }
    }
  }

  /**
   * Initialize store cards with Google Places data
   */
  async function initializeStoreCards() {
    const storeCards = document.querySelectorAll('.store-card-revamp');
    
    if (storeCards.length === 0) {
      return;
    }

    // Initialize config
    initConfig();
    
    // Process each store card
    for (const storeCard of storeCards) {
      storeCard.classList.add('store-card-loading');
      
      const storeIndex = storeCard.getAttribute('data-store-index');
      const placeId = storeCard.getAttribute('data-store-place-id');
      const storeName = storeCard.getAttribute('data-store-name');
      const storeAddress = storeCard.getAttribute('data-store-address');


      // Initialize directions button IMMEDIATELY with address fallback (before attaching card navigation)
      if (storeIndex) {
        initializeDirectionsButtonFallback(storeCard, storeIndex);
      }
      
      // Attach card navigation AFTER directions button is initialized
      attachCardNavigation(storeCard);

      if (placeId && placeId.trim() !== '') {
        // Fetch place details using place_id
        const placeData = await fetchPlaceDetails(placeId, storeName, storeAddress);
        
        if (placeData) {
          updateStoreCard(storeCard, placeData);
        } else {
          console.warn(`[Store Places API] No place data returned for: ${storeName}`);
          // Remove loading state even if fetch failed
          storeCard.classList.remove('store-card-loading');
        }
      } else {
        // No place_id, try to search by name/address
        // For now, just remove loading state
        storeCard.classList.remove('store-card-loading');
        console.warn(`[Store Places API] ⚠️ No place_id for store: ${storeName}. Please add Google Place ID to metaobject.`);
      }

      // Add small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Initialize when DOM is ready
   */
  function initStoreScripts() {
    initializeStoreCards();
    initializeStoreDetail();
    
    // Ensure directions link is updated on page load (for dummy data)
    setTimeout(() => {
      const detailRoot = document.getElementById('store-detail-section');
      if (detailRoot) {
        updateDirectionsLinkFromAddress(detailRoot);
      }
    }, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStoreScripts);
  } else {
    initStoreScripts();
  }

})();

